import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface SaleReturnItem {
  id: string;
  sale_return_id: string;
  sale_item_id: string;
  item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  created_at: string;
}

export interface SaleReturn {
  id: string;
  sale_id: string;
  clinic_id: string;
  customer_id: string;
  status: "COMPLETED" | "CANCELLED";
  reason: string;
  subtotal: number;
  total: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  items: SaleReturnItem[];
}

export interface CreateSaleReturnItemInput {
  sale_item_id: string;
  quantity: number;
}

export function useSaleReturns(saleId: string | null, enabled = true) {
  return useQuery<SaleReturn[]>({
    queryKey: ["sale-returns", saleId],
    enabled: enabled && !!saleId,
    queryFn: async () => {
      const { data: returns, error: returnsError } = await supabase
        .from("sale_returns")
        .select("*")
        .eq("sale_id", saleId)
        .order("created_at", { ascending: false });

      if (returnsError) throw returnsError;

      const returnIds = ((returns ?? []) as Array<{ id: string }>).map((row) => row.id);
      const { data: items, error: itemsError } = returnIds.length
        ? await supabase
            .from("sale_return_items")
            .select("*")
            .in("sale_return_id", returnIds)
            .order("created_at", { ascending: true })
        : { data: [], error: null };

      if (itemsError) throw itemsError;

      const itemsByReturnId = new Map<string, SaleReturnItem[]>();
      for (const item of (items ?? []) as SaleReturnItem[]) {
        const list = itemsByReturnId.get(item.sale_return_id) ?? [];
        list.push(item);
        itemsByReturnId.set(item.sale_return_id, list);
      }

      return ((returns ?? []) as Omit<SaleReturn, "items">[]).map((row) => ({
        ...row,
        items: itemsByReturnId.get(row.id) ?? [],
      }));
    },
  });
}

export function useCreateSaleReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      saleId,
      reason,
      items,
    }: {
      saleId: string;
      reason: string;
      items: CreateSaleReturnItemInput[];
    }) => {
      const { data, error } = await supabase.rpc("create_sale_return", {
        p_sale_id: saleId,
        p_reason: reason,
        p_items: items.map(({ sale_item_id, quantity }) => ({ sale_item_id, quantity })),
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: (returnId, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sale-detail", variables.saleId] });
      queryClient.invalidateQueries({ queryKey: ["sale-returns", variables.saleId] });
      queryClient.invalidateQueries({ queryKey: ["sales-list"] });
      queryClient.invalidateQueries({ queryKey: ["stock-balances"] });
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      toast({ title: "Devolução registrada", description: "O estoque dos produtos foi atualizado." });
      return returnId;
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao registrar devolução",
        description: error.message || "Não foi possível registrar a devolução.",
        variant: "destructive",
      });
    },
  });
}
